import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  palette,
  radius,
  shadow,
  spacing,
  typography,
  zIndex,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { useDialogFocus } from '../hooks/use-dialog-focus';
import { AppButton, AppButtonVariant } from '../button/app-button';
import { ModalPortal } from './modal-portal';
import { Presence, m, scrimMotion, useOverlayMotion } from '../../motion';

/* Portaled to <body> (like BrandedLoader): the wizard pane's animated
 * ancestors create transform containing blocks that would trap a fixed
 * overlay inside the pane.
 *
 * `modalTop`, not `overlay`. This dialog is almost always raised FROM another
 * surface — "remove this unit" from inside a unit form, "delete this building"
 * from inside the building modal — and those scrims sit at `zIndex.modal`
 * (2100). At `overlay` (2000) the confirmation painted UNDERNEATH the modal
 * that asked for it.
 *
 * MOTION. The scrim fades (scrimMotion) and the card grows from the centre
 * (overlay kind 'modal', a bottom sheet under `sm`), and BOTH leave the way
 * they came: the `open` conditional sits inside a Presence, so flipping it to
 * false plays the exit before the portal unmounts. Callers keep passing `open`
 * and nothing else changes for them. */
const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modalTop};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
  background: ${palette.overlay};

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

const Card = m.div`
  width: 100%;
  /* 480, not 440: the widest real pair of action labels ("Keep my
     subscription" + "Cancel subscription") measures ~383px with its gap, and
     440px left the row 374px of content — nine pixels short, which is what
     wrapped the buttons into a stack. */
  max-width: 480px;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  padding: ${spacing.lg};

  ${down('sm')} {
    max-width: none;
    max-height: calc(100vh - ${spacing.sm});
    max-height: calc(100dvh - ${spacing.sm});
    overflow-y: auto;
    border-radius: ${radius.lg} ${radius.lg} 0 0;
    padding: ${spacing.md} ${spacing.sm};
    /* The safe area, so the actions are not under a home indicator. Added to
       the bottom padding the sheet already had, which matches its top. */
    padding-bottom: calc(${spacing.md} + var(--safe-bottom));
  }
`;

const Title = styled.h2`
  margin: 0 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: -0.014em;
  color: ${palette.ink};
`;

const Message = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const Extra = styled.div`
  margin: 0 0 ${spacing.md};
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${spacing.xs};

  /* THE SAFE EXIT LIVES LEFT, THE DECISION LIVES RIGHT. Two long labels
     ("Keep my subscription" / "Cancel subscription") used to trip flex-wrap
     inside the 440px card and stack on top of each other at flex-end — a
     layout nobody designed. The dismiss button is always rendered first, so
     pushing it left both matches the design and gives the row back the width
     it was wasting on right-alignment. */
  & > button:first-of-type {
    margin-right: auto;
  }

  ${down('sm')} {
    /* Where the row genuinely cannot fit, stack deliberately: full-width
       buttons, the decisive action on top, the safe exit under it. */
    flex-direction: column-reverse;
    align-items: stretch;

    & > button:first-of-type {
      margin-right: 0;
    }
  }
`;

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  primaryLabel: string;
  primaryVariant?: AppButtonVariant;
  onPrimary: () => void | Promise<void>;
  secondaryLabel?: string;
  onSecondary?: () => void;
  dismissLabel?: string;
  onDismiss: () => void;
  /** A field the decision needs (e.g. an optional note), under the message. */
  children?: React.ReactNode;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  primaryLabel,
  primaryVariant = 'primary',
  onPrimary,
  secondaryLabel,
  onSecondary,
  dismissLabel = 'Never mind',
  onDismiss,
  children,
}) => {
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const handleDismiss = () => {
    if (!busyRef.current) {
      onDismiss();
    }
  };
  const handlePrimary = async () => {
    if (busyRef.current) {
      return;
    }

    busyRef.current = true;
    setBusy(true);
    try {
      await onPrimary();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const dialogRef = useDialogFocus(open, handleDismiss);
  const cardMotion = useOverlayMotion('modal');

  return (
    <Presence>
      {open ? (
        <ModalPortal key="confirm-dialog">
          <Scrim {...scrimMotion} onClick={handleDismiss}>
            <Card
              {...cardMotion}
              ref={dialogRef}
              tabIndex={-1}
              role="alertdialog"
              aria-modal="true"
              aria-label={title}
              onClick={e => e.stopPropagation()}
            >
              <Title>{title}</Title>
              <Message>{message}</Message>
              {children ? <Extra>{children}</Extra> : null}
              <Actions>
                {/* THE SAFE OPTION TAKES THE FOCUS. A confirmation that opens
                    with the keyboard already on the destructive button is a
                    confirmation that can be dismissed by pressing Enter twice
                    — which is exactly how somebody deletes a portfolio they
                    meant to keep. */}
                <AppButton
                  variant="ghost"
                  disabled={busy}
                  data-autofocus
                  onClick={handleDismiss}
                >
                  {dismissLabel}
                </AppButton>
                {secondaryLabel && onSecondary ? (
                  <AppButton
                    variant="secondary"
                    disabled={busy}
                    onClick={onSecondary}
                  >
                    {secondaryLabel}
                  </AppButton>
                ) : null}
                <AppButton
                  variant={primaryVariant}
                  loading={busy}
                  loadingLabel={`${primaryLabel} in progress`}
                  onClick={handlePrimary}
                >
                  {primaryLabel}
                </AppButton>
              </Actions>
            </Card>
          </Scrim>
        </ModalPortal>
      ) : null}
    </Presence>
  );
};
